/**
 * Outbox dead-letter queue (DLQ) operator view.
 *
 * Lists rows the dispatcher has dead-lettered after exhausting the retry
 * budget, and exposes a `Replay` button that re-enqueues a single row.
 * Replay clears `dead_lettered_at` / `next_attempt_at` / `last_error`
 * and resets `attempt_count` to zero so the dispatcher picks the row
 * up immediately with a fresh retry budget. The action is audited.
 *
 * Permission: `outbox.replay` (granted only to `platform_admin` by
 * default). The whole page is gated on the permission via the sidebar
 * + the backend's PermissionGuard.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { OutboxEvent } from "@dfp/api-client";
import { Badge } from "../components/Badge.js";
import { Button } from "../components/Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { Table, TBody, THead, Th, Td, Tr } from "../components/Table.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";

export function OutboxDlqPage(): JSX.Element {
  const auth = useAuth();
  const canReplay = (auth.user?.permissions ?? []).includes("outbox.replay");
  const qc = useQueryClient();

  const rows = useQuery({
    queryKey: ["outbox", "dead-lettered"],
    queryFn: () => api.get<readonly OutboxEvent[]>("/api/admin/outbox/dead-lettered?limit=100"),
  });

  const replay = useMutation({
    mutationFn: (id: string) =>
      api.post<OutboxEvent>(`/api/admin/outbox/dead-lettered/${id}/replay`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["outbox", "dead-lettered"] });
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Outbox dead-letter queue</h1>
        <p className="text-sm text-slate-500">
          Events the dispatcher gave up on after exhausting the retry budget. Replay re-enqueues a
          single row with a fresh attempt count.
        </p>
      </div>

      {replay.isError ? (
        <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
          Replay failed: {(replay.error as Error).message}
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Dead-lettered events</CardTitle>
          <span className="text-xs text-slate-500">{rows.data?.length ?? 0} rows</span>
        </CardHeader>
        <CardBody className="p-0">
          {rows.isLoading ? (
            <p className="p-5 text-sm text-slate-500">Loading…</p>
          ) : rows.isError ? (
            <p className="p-5 text-sm text-red-700">Failed to load DLQ.</p>
          ) : (rows.data ?? []).length === 0 ? (
            <p className="p-5 text-sm text-slate-500">No dead-lettered events. 🎉</p>
          ) : (
            <Table>
              <THead>
                <tr>
                  <Th>Dead-lettered</Th>
                  <Th>Aggregate</Th>
                  <Th>Event</Th>
                  <Th>Attempts</Th>
                  <Th>Last error</Th>
                  <Th>{""}</Th>
                </tr>
              </THead>
              <TBody>
                {rows.data?.map((r) => (
                  <Tr key={r.id}>
                    <Td className="text-xs text-slate-500">
                      {r.deadLetteredAt ? new Date(r.deadLetteredAt).toLocaleString() : "—"}
                    </Td>
                    <Td className="text-xs">
                      <Badge tone="neutral">{r.aggregateType}</Badge>{" "}
                      <span className="font-mono text-slate-500">{r.aggregateId.slice(0, 8)}…</span>
                    </Td>
                    <Td className="text-xs">{r.eventType}</Td>
                    <Td className="text-xs text-slate-500">{r.attemptCount}</Td>
                    <Td className="text-xs text-slate-500 max-w-[24rem] truncate">
                      <span title={r.lastError ?? ""}>{r.lastError ?? "—"}</span>
                    </Td>
                    <Td className="text-right">
                      {canReplay ? (
                        <Button
                          variant="secondary"
                          className="px-3 py-1 text-xs"
                          disabled={replay.isPending && replay.variables === r.id}
                          onClick={() => replay.mutate(r.id)}
                        >
                          {replay.isPending && replay.variables === r.id ? "Replaying…" : "Replay"}
                        </Button>
                      ) : null}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
